import React from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Youtube, ExternalLink, ArrowUpRight } from "lucide-react";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { NextEventFooterBlock } from "@/components/NextEventBanner";
import { trackEvent } from "@/lib/analytics";

const track = (name, params) => { try { trackEvent(name, params); } catch (_) {} };

const SEO_TAGS = [
  "DJ mariage Hauts-de-France",
  "DJ mariage Nord",
  "DJ mariage Pas-de-Calais",
  "DJ événementiel Lille",
  "Photobooth mariage Nord",
  "DJ Tourcoing · Roubaix",
  "DJ Bousbecque · Armentières",
  "DJ mairie · entreprise",
];

const SOCIALS = [
  { name: "Instagram", Icon: Instagram, url: "https://www.instagram.com/myanimationdj/" },
  { name: "Facebook", Icon: Facebook, url: "https://www.facebook.com/p/Dj-Paps-MY-Animation-61574555792594/" },
  { name: "YouTube", Icon: Youtube, url: "https://www.youtube.com/@yannickmulier2998" },
];

const PARTNERS = [
  { name: "Mariages.net", url: "https://www.mariages.net/musique-mariage/my-animation--e386697" },
  { name: "Bark", url: "https://www.bark.com/fr/fr/b/my-animation-/N03P4q/" },
];

const NAV_PRESTATIONS = [
  { label: "Mariage", to: "/mariage" },
  { label: "Anniversaire", to: "/anniversaire" },
  { label: "Entreprises", to: "/entreprises" },
  { label: "Bars & Restaurants", to: "/dj-bar-restaurant-lille" },
  { label: "Collectivités & Mairies", to: "/collectivites" },
  { label: "Associations", to: "/associations" },
  { label: "Baptêmes & Communions", to: "/baptemes-communions" },
];

const NAV_PLUS = [
  { label: "Photobooth", to: "/photobooth" },
  { label: "Location de matériel", to: "/location" },
  { label: "Blind Test & Karaoké", to: "/blind-test-karaoke" },
  { label: "Chanteur Live · Maxence Berche", to: "/maxence-berche" },
  { label: "Résidences Seniors / EHPAD", to: "/seniors" },
  { label: "Galerie", to: "/galerie" },
  { label: "Blog", to: "/blog" },
  { label: "Avis", to: "/avis" },
  { label: "FAQ", to: "/faq" },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#050505] border-t border-[#DCB457]/15 overflow-hidden"
    >
      {/* glow décoratif */}
      <div className="pointer-events-none absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[300px] rounded-full bg-[#DCB457]/[0.05] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 py-20">
        {/* Signature brand */}
        <div className="border-b border-[#DCB457]/12 pb-12 mb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
          <div className="lg:col-span-8">
            <h2 className="font-serif-luxe text-[3.5rem] md:text-[6rem] lg:text-[8rem] leading-[0.95] tracking-tight">
              <span className="gold-text">My Animation</span>
            </h2>
            <p className="mt-3 italic text-white/55 font-serif-luxe text-xl md:text-2xl">
              Créateur d'ambiance et d'événement
            </p>
            <p className="mt-4 max-w-2xl text-white/55 text-sm md:text-base leading-relaxed">
              DJ événementiel haut de gamme — mariages, anniversaires, entreprises,
              mairies et événements publics dans les Hauts-de-France, le Nord, le
              Pas-de-Calais et la Belgique.
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/contact"
              data-testid="footer-cta-devis"
              className="btn-gold inline-flex items-center gap-2"
            >
              Demander un devis <ArrowUpRight size={16} strokeWidth={1.4} />
            </Link>
            <p className="mt-3 text-[11px] uppercase tracking-[0.22em] text-white/45">
              Réponse sous 24h · Devis gratuit
            </p>
          </div>
        </div>

        {/* 4 colonnes équilibrées */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Col 1 — Contact + Réseaux */}
          <div className="lg:col-span-3">
            <p className="eyebrow mb-4">Contact</p>
            <ul className="space-y-3 text-white/75 text-sm">
              <li className="flex items-center gap-3">
                <Phone size={16} strokeWidth={1.2} className="text-[#DCB457] shrink-0" />
                <a href="tel:0769224956" onClick={() => track("click_phone", { location: "footer" })} data-testid="footer-phone" className="hover:text-[#DCB457] transition">
                  07 69 22 49 56
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail size={16} strokeWidth={1.2} className="text-[#DCB457] shrink-0" />
                <a
                  href="mailto:my-animation@outlook.fr"
                  onClick={() => track("click_email", { location: "footer" })}
                  data-testid="footer-email"
                  className="hover:text-[#DCB457] transition break-all"
                >
                  my-animation@outlook.fr
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.2} className="text-[#DCB457] shrink-0 mt-1" />
                <span className="leading-relaxed">
                  Hauts-de-France · Nord<br />Pas-de-Calais · Belgique
                </span>
              </li>
            </ul>

            <p className="eyebrow mt-8 mb-4">Suivez-nous</p>
            <NextEventFooterBlock />
            <div className="flex items-center gap-3">
              {SOCIALS.map(({ name, Icon, url }) => (
                <a
                  key={name}
                  href={url}
                  onClick={() => track("outbound_click", { location: "footer_social", outbound_domain: (new URL(url)).hostname })}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`${name} My Animation`}
                  data-testid={`footer-social-${name.toLowerCase()}`}
                  className="group w-11 h-11 border border-[#DCB457]/30 flex items-center justify-center hover:bg-[#DCB457] hover:border-[#DCB457] transition-all duration-300"
                >
                  <Icon
                    size={17}
                    strokeWidth={1.2}
                    className="text-[#DCB457] group-hover:text-black transition-colors"
                  />
                </a>
              ))}
            </div>

            <p className="eyebrow mt-8 mb-3">Référencé sur</p>
            <div className="flex flex-wrap gap-2">
              {PARTNERS.map((p) => (
                <a
                  key={p.name}
                  href={p.url}
                  onClick={() => track("outbound_click", { location: "footer_partner", outbound_domain: (new URL(p.url)).hostname })}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`footer-partner-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.16em] text-white/55 border border-[#DCB457]/20 px-3 py-1.5 hover:text-[#DCB457] hover:border-[#DCB457]/60 transition"
                >
                  {p.name} <ExternalLink size={10} strokeWidth={1.4} />
                </a>
              ))}
            </div>
          </div>

          {/* Col 2 — Prestations */}
          <div className="lg:col-span-3">
            <p className="eyebrow mb-4">Prestations</p>
            <ul className="space-y-2.5 text-sm text-white/65">
              {NAV_PRESTATIONS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="inline-flex items-center gap-2 hover:text-[#DCB457] transition group"
                  >
                    <span className="w-3 h-px bg-[#DCB457]/30 group-hover:bg-[#DCB457] group-hover:w-5 transition-all" />
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Col 3 — Animations & Plus */}
          <div className="lg:col-span-3">
            <p className="eyebrow mb-4">Animations & Plus</p>
            <ul className="space-y-2.5 text-sm text-white/65">
              {NAV_PLUS.map((item) => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    className="inline-flex items-center gap-2 hover:text-[#DCB457] transition group"
                  >
                    <span className="w-3 h-px bg-[#DCB457]/30 group-hover:bg-[#DCB457] group-hover:w-5 transition-all" />
                    {item.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/espace-client"
                  data-testid="footer-espace-client"
                  className="inline-flex items-center gap-2 hover:text-[#DCB457] transition group"
                >
                  <span className="w-3 h-px bg-[#DCB457]/30 group-hover:bg-[#DCB457] group-hover:w-5 transition-all" />
                  Espace client
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 4 — Zones d'intervention */}
          <div className="lg:col-span-3">
            <p className="eyebrow mb-4">Zones d'intervention</p>
            <div className="flex flex-wrap gap-2">
              {SEO_TAGS.map((tag) => (
                <span
                  key={tag}
                  className="text-[10px] uppercase tracking-[0.16em] text-white/55 border border-[#DCB457]/15 px-3 py-1.5 hover:text-[#DCB457] hover:border-[#DCB457]/40 transition"
                >
                  {tag}
                </span>
              ))}
            </div>
            <p className="mt-5 text-[11px] uppercase tracking-[0.22em] text-[#DCB457]/80">
              +30 villes desservies
            </p>
            <p className="mt-2 text-xs text-white/45 leading-relaxed">
              Lille · Tourcoing · Roubaix · Armentières · Dunkerque · Arras · Lens · Béthune · Valenciennes · Calais · Bousbecque · Wervicq-Sud · et toute la Belgique frontalière.
            </p>
          </div>
        </div>

        {/* Barre légale */}
        <div className="mt-16 pt-8 border-t border-[#DCB457]/12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} <span className="text-white/60">My Animation</span> · Tous droits réservés · SIRET 532 387 156 00039
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/40">
            <Link to="/mentions-legales" data-testid="footer-mentions-legales" className="hover:text-[#DCB457] transition">
              Mentions légales
            </Link>
            <span className="text-white/20">·</span>
            <Link to="/politique-confidentialite" data-testid="footer-politique-confidentialite" className="hover:text-[#DCB457] transition">
              Politique de confidentialité
            </Link>
            <span className="text-white/20">·</span>
            <button
              type="button"
              onClick={() => window.dispatchEvent(new Event("ma:open-consent"))}
              data-testid="footer-manage-cookies"
              className="hover:text-[#DCB457] transition text-xs"
            >
              Gérer mes cookies
            </button>
            <span className="text-white/20">·</span>
            <Link to="/contact" className="hover:text-[#DCB457] transition">
              Contact
            </Link>
            <span className="text-white/20">·</span>
            <LanguageSwitcher variant="footer" />
          </div>
        </div>
      </div>
    </footer>
  );
}
